import React, { useState } from 'react';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    IconButton,  
    Tooltip,
    Dialog, 
    DialogTitle, 
    DialogContent, 
} from '@mui/material';
import { Close, Share, ContentCopy, Check } from '@mui/icons-material';
import FlexBetween from 'components/FlexBetween';


const ChartDialog = ({ 
    open, 
    onClose, 
    chartComponent 
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [copied, setCopied] = useState(false);
    
    const downloadImage = (blob) => {
        try {
            const timestamp = new Date().toISOString().replace(/[:.-]/g, "_"); // Format: YYYY_MM_DDTHH_MM_SS
            const fileName = `chart_${timestamp}.png`; 
    
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName; 
            link.click();
        
            // Clean up the object URL
            URL.revokeObjectURL(link.href);
            toast.success("Chart downloaded.");
        } catch (error) {
            toast.error(error?.message || "Failed to download image!");
        }        
    };   
    
    const copyImageToClipboard = async (blob) => {
        try {
            const clipboardItem = new ClipboardItem({ "image/png": blob });
            await navigator.clipboard.write([clipboardItem]);
            setCopied(true);
            toast.success("Chart copied to clipboard.");
        } catch (error) {            
            downloadImage(blob);
        }
    };

    const handleShare = async () => {
        try {
            const chartContent = document.getElementById('chart-content');
            if (!chartContent) {
                toast.error('Chart not found!');
                return;
            }

            const canvas = await html2canvas(chartContent, {
                allowTaint: true,
                useCORS: true,
            });

            const blob = await new Promise((resolve, reject) => {
                canvas.toBlob(b => b ? resolve(b) : reject(new Error('Create image failed!')));
            });

            const timestamp = new Date().toISOString().replace(/[:.-]/g, "_");
            const file = new File([blob], `chart_${timestamp}.png`, {
                type: 'image/png',
            });

            if (
                navigator.canShare &&
                navigator.canShare({ files: [file] })
            ) {
                await navigator.share({
                    files: [file],
                    title: 'Chart',
                    // text: 'Shared from DigitAir',
                });
                
                return;
            }

            toast.info('Sharing not supported. Copying image...');
            await copyImageToClipboard(blob);

        } catch (error) {
            toast.error(error?.message || 'Failed to share image');
        }
    };

    const handleCopyToClipboard = async () => {
        try {            
            const chartContent = document.getElementById('chart-content');
            if (!chartContent) {
                toast.error('Chart not found!');
                return;
            }
            
            const canvas = await html2canvas(chartContent, {
                allowTaint: true,   
                useCORS: true,      
            });

            const blob = await new Promise((resolve, reject) => {
                canvas.toBlob((blobResult) => {
                    if (blobResult) 
                        resolve(blobResult);
                    else 
                        reject(new Error('Failed to create Blob from canvas!'));
                });
            });
           
            if (!navigator.clipboard || !window.ClipboardItem) {
                toast.info("Clipboard access not supported. Downloading image...");
                downloadImage(blob);
                return;
            }
           
            copyImageToClipboard(blob);
        } catch (error) {
            toast.error(error?.message || "Failed to copy image!");
        } finally {
            setTimeout(() => setCopied(false), 3000);        }

    };
    

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '96%' : '94%' } }}
        >
            <DialogTitle>
                <FlexBetween>
                    <Typography>Chart</Typography>

                    <Box display="flex" alignItems="center" gap={1}>
                        {/* SHARE */}
                        <Tooltip title="Share" arrow>
                            <IconButton
                                color="primary"
                                onClick={handleShare}
                            >
                                <Share />
                            </IconButton>
                        </Tooltip>

                        {/* COPY */}
                        <Tooltip title={copied ? "Copied" : "Copy"} arrow>
                            <IconButton
                                color={copied ? "success" : "primary"}
                                onClick={handleCopyToClipboard}
                            >
                                {copied ? <Check /> : <ContentCopy />}
                            </IconButton>
                        </Tooltip>

                        {/* CLOSE */}
                        <IconButton
                            aria-label="close"
                            edge="end"
                            onClick={onClose}
                            sx={{ color: 'neutral.dark' }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                </FlexBetween>
            </DialogTitle>

            <DialogContent>
                <div id="chart-content">
                    {chartComponent 
                        ? chartComponent 
                        : <Typography>No charts available</Typography>
                    }
                </div>
            </DialogContent>            
        </Dialog>
    );
};

export default ChartDialog;
